import { useCallback, useState } from 'react'

import {
  type MarkingScheme,
  type TaskDraft,
  fetchDocumentPages,
  rangesAsText,
  rangesIn,
} from '../../api/exams'
import { type CardFilter, type Flashcard, fetchCards, fetchTags } from '../../api/flashcards'
import { useLoad } from '../../api/useLoad'
import { ContentPicker } from '../../ui/ContentPicker'
import { Button } from '../../ui/Control'
import { RichText } from '../../ui/RichText'
import { MARKDOWN, nameOfMarkup } from '../../ui/markup'
import { Select } from '../../ui/Select'
import { TagModeSwitch, matches as cardMatches } from '../Cards'
import { Question } from './Question'

/**
 * A card of a fixed set, as much as is needed to show what was chosen — no second fetch.
 *
 * {@link label} is whatever best identifies the card: the picker's own row gives the cue it is
 * asked for, or else its first line; reopening an existing task gives the card's actual front. Both
 * are display-only — only the id is ever sent back.
 */
export interface ChosenCard {
  readonly id: string
  readonly label: string
}

/** What to call a card taken over from a filter's result — the same rule the picker's row uses. */
function labelOf(card: Flashcard): string {
  if (card.askedFor !== '') {
    return card.askedFor
  }
  const firstLine = card.front.split('\n').find((line) => line.trim() !== '')
  return firstLine ?? ''
}

/**
 * One task of a test, while it is being written.
 *
 * Two decisions are made on this panel and both are the user's, so both are a visible control rather
 * than an inference:
 *
 * **What is asked** — pages of something already in the library, or a text written here. The two are
 * not variants of one field: the first is chosen through the same search ⌘K uses, and the second is
 * the same editor everything else in MyLib is written in.
 *
 * **By what standard it is measured** — a marking scheme, picked from *every* scheme in the space.
 * Any subject's, deliberately: a test can be about two subjects at once, and the test's own subject
 * only decides which schemes stand at the top of the list.
 */
export interface TaskEditorProps {
  readonly draft: TaskDraft
  readonly position: number
  readonly schemes: readonly MarkingScheme[]
  /** What the document is called, where one is chosen — kept beside the draft for the heading. */
  readonly documentTitle?: string | null
  /** The cards of a fixed set, labelled — kept beside the draft for the same reason. */
  readonly cards?: readonly ChosenCard[]
  readonly onChange: (
    draft: TaskDraft,
    documentTitle?: string | null,
    cards?: readonly ChosenCard[],
  ) => void
  readonly onRemove: () => void
  readonly onMove: (by: -1 | 1) => void
  readonly canMoveUp: boolean
  readonly canMoveDown: boolean
}

export function TaskEditor({
  draft,
  position,
  schemes,
  documentTitle = null,
  cards = [],
  onChange,
  onRemove,
  onMove,
  canMoveUp,
  canMoveDown,
}: TaskEditorProps) {
  const [picking, setPicking] = useState(false)
  // What is in the field, kept apart from the ranges it means: "3-" is a thing somebody is typing,
  // and re-writing it as they type would move the cursor out from under them.
  const [typedPages, setTypedPages] = useState(rangesAsText(draft.pages ?? []))
  const documentId = draft.documentId ?? null
  // How long the document is, so the field can say what there is to choose from. Asked once per
  // document: it means opening the file, and the answer does not change while somebody types. A
  // document whose length cannot be read is not a reason to stop somebody naming pages of it, so a
  // failure here simply says nothing.
  const length = useLoad(
    useCallback(
      async (signal: AbortSignal) =>
        documentId === null ? null : fetchDocumentPages(documentId, signal),
      [documentId],
    ),
  )
  const howManyPages = length.load.status === 'ready' ? (length.load.value?.pages ?? null) : null

  const tags = useLoad(useCallback((signal: AbortSignal) => fetchTags(signal), []))
  const knownTags = tags.load.status === 'ready' ? tags.load.value.map((tag) => tag.name) : []

  // Every card in the space, fetched once and matched here — the same way the round a learning
  // session starts from is put together. Asked for lazily: a task that is not a card set has no
  // use for it.
  const allCards = useLoad(useCallback((signal: AbortSignal) => fetchCards(signal), []))

  const written = draft.kind === 'written'
  const cardset = draft.kind === 'cardset'
  const dynamic = draft.cardSetMode === 'dynamic'

  const filter: CardFilter = {
    query: draft.cardQuery ?? '',
    tags: draft.cardTags ?? [],
    tagMode: draft.cardTagMode ?? 'all',
  }
  const matchingCards =
    allCards.load.status === 'ready'
      ? allCards.load.value.filter((card) => cardMatches(card, filter))
      : []
  const notYetChosen = matchingCards.filter((card) => !cards.some((one) => one.id === card.id))

  return (
    <li className="exam-task">
      <header className="exam-task__head">
        <h3 className="exam-task__title">Task {position + 1}</h3>
        <div className="exam-task__controls">
          <Button
            icon="up"
            iconOnlyLabel="Move this task up"
            disabled={!canMoveUp}
            onClick={() => {
              onMove(-1)
            }}
          />
          <Button
            icon="down"
            iconOnlyLabel="Move this task down"
            disabled={!canMoveDown}
            onClick={() => {
              onMove(1)
            }}
          />
          <Button icon="close" iconOnlyLabel="Remove this task" onClick={onRemove} />
        </div>
      </header>

      <Select
        label="Marked by"
        value={draft.schemeId}
        options={schemes.map((scheme) => ({
          value: scheme.id,
          label: `${scheme.name} — ${scheme.subjectName}`,
        }))}
        hint="What matters in this kind of task, and on what scale. Written per subject."
        onChange={(schemeId) => {
          onChange({ ...draft, schemeId }, documentTitle)
        }}
      />

      <Select
        label="The question is"
        value={draft.kind}
        options={[
          { value: 'document', label: 'Pages of a document' },
          {
            value: 'written',
            label: `A text written here (${nameOfMarkup(draft.markup ?? MARKDOWN)})`,
          },
          { value: 'cardset', label: 'A set of flashcards' },
        ]}
        onChange={(kind) => {
          if (kind === 'written') {
            onChange({ ...draft, kind: 'written' }, documentTitle)
          } else if (kind === 'cardset') {
            onChange(
              { ...draft, kind: 'cardset', cardSetMode: draft.cardSetMode ?? 'fixed' },
              null,
              cards,
            )
          } else {
            onChange({ ...draft, kind: 'document' }, documentTitle)
          }
        }}
      />

      {written ? (
        <RichText
          text={draft.text ?? ''}
          format={draft.markup ?? MARKDOWN}
          onFormat={(wanted) => {
            onChange({ ...draft, markup: wanted }, documentTitle)
            return Promise.resolve()
          }}
          label={`The question of task ${String(position + 1)}`}
          placeholder="What is asked…"
          purpose="exam-task"
          onChange={(text) => {
            onChange({ ...draft, text }, documentTitle)
          }}
        >
          {/*
            A draft is only written down when the whole test is saved, so switching here is a local
            change like any other field of it — nothing to send, nothing to wait for.
          */}
        </RichText>
      ) : cardset ? (
        <div className="exam-task__cardset">
          <Select
            label="How the set is chosen"
            value={draft.cardSetMode ?? 'fixed'}
            options={[
              { value: 'fixed', label: 'Cards chosen once' },
              { value: 'dynamic', label: 'A filter, run again each time it is sat' },
            ]}
            onChange={(mode) => {
              onChange(
                { ...draft, cardSetMode: mode === 'dynamic' ? 'dynamic' : 'fixed' },
                null,
                cards,
              )
            }}
          />

          {/*
            The same filter, whichever mode is chosen above — finding a card is one question, and
            what happens with what it finds is the only thing the mode decides. A fixed set takes
            the filter's result over once; a dynamic one keeps the filter itself and runs it again
            every time the task is sat.
          */}
          <label className="field">
            <span className="field__label">Search the cards</span>
            <input
              className="field__control"
              value={draft.cardQuery ?? ''}
              placeholder="Words in the cue or either side"
              onChange={(event) => {
                onChange({ ...draft, cardQuery: event.target.value }, null, cards)
              }}
            />
          </label>

          {knownTags.length > 0 && (
            <fieldset className="filter-tags">
              <legend className="card-field__label">Narrow by tag</legend>
              <div className="filter-tags__choices">
                {knownTags.map((tag) => (
                  <label key={tag} className="filter-chip">
                    <input
                      className="choice-input"
                      type="checkbox"
                      checked={(draft.cardTags ?? []).includes(tag)}
                      onChange={(event) => {
                        const before = draft.cardTags ?? []
                        onChange(
                          {
                            ...draft,
                            cardTags: event.target.checked
                              ? [...before, tag]
                              : before.filter((one) => one !== tag),
                          },
                          null,
                          cards,
                        )
                      }}
                    />
                    <span>{tag}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {(draft.cardTags ?? []).length > 0 && (
            <TagModeSwitch
              value={draft.cardTagMode ?? 'all'}
              onChange={(tagMode) => {
                onChange({ ...draft, cardTagMode: tagMode }, null, cards)
              }}
            />
          )}

          {dynamic ? (
            <p className="exam-task__cardsetNote">
              {matchingCards.length} card{matchingCards.length === 1 ? '' : 's'} match this filter
              right now — which cards that is can change by the next sitting.
            </p>
          ) : (
            <>
              <p className="exam-task__cardsetNote">
                {notYetChosen.length} card{notYetChosen.length === 1 ? '' : 's'} match this filter
                and {notYetChosen.length === 1 ? 'is' : 'are'} not in the set yet.
              </p>

              {notYetChosen.length > 0 && (
                <Button
                  onClick={() => {
                    const added = [
                      ...cards,
                      ...notYetChosen.map((card) => ({ id: card.id, label: labelOf(card) })),
                    ]
                    onChange({ ...draft, cardIds: added.map((one) => one.id) }, null, added)
                  }}
                >
                  Take over {notYetChosen.length} matching card
                  {notYetChosen.length === 1 ? '' : 's'}
                </Button>
              )}

              <ul className="exam-task__cards">
                {cards.map((card) => (
                  <li key={card.id} className="exam-task__card">
                    <span>{card.label}</span>
                    <Button
                      icon="close"
                      iconOnlyLabel="Remove this card from the set"
                      onClick={() => {
                        const kept = cards.filter((one) => one.id !== card.id)
                        onChange({ ...draft, cardIds: kept.map((one) => one.id) }, null, kept)
                      }}
                    />
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => {
                  setPicking(true)
                }}
              >
                Add one card by its own search
              </Button>

              {picking && (
                <ContentPicker
                  kinds={['flashcard']}
                  placeholder="Which card?"
                  label="Find a card to add to this set"
                  lead="Searches the text inside everything in the library."
                  already={cards.map((card) => card.id)}
                  onChoose={(chosen) => {
                    const added = [...cards, { id: chosen.id, label: chosen.label }]
                    onChange({ ...draft, cardIds: added.map((one) => one.id) }, null, added)
                  }}
                  onClose={() => {
                    setPicking(false)
                  }}
                />
              )}
            </>
          )}
        </div>
      ) : (
        <div className="exam-task__source">
          <p className="exam-task__document">
            {documentTitle ?? 'No document chosen yet'}
            <Button
              onClick={() => {
                setPicking(true)
              }}
            >
              {draft.documentId === null || draft.documentId === undefined ? 'Choose' : 'Another'}
            </Button>
          </p>

          <label className="field">
            <span className="field__label">Pages</span>
            <input
              className="field__control"
              value={typedPages}
              inputMode="numeric"
              placeholder="3-5, 12"
              onChange={(event) => {
                setTypedPages(event.target.value)
                onChange({ ...draft, pages: rangesIn(event.target.value) }, documentTitle)
              }}
            />
            <span className="field__hint">
              A run, several runs, or single pages. They need not follow one another.
              {howManyPages !== null && ` This document has ${String(howManyPages)}.`}
            </span>
          </label>

          {documentId !== null && (
            <Question
              prompt={{
                kind: 'document',
                documentId,
                documentTitle,
                pages: draft.pages ?? [],
              }}
              label={`The pages of task ${String(position + 1)}`}
            />
          )}

          {picking && (
            <ContentPicker
              kinds={['document']}
              placeholder="Which document?"
              label="Find the document this task is made of"
              lead="Searches the text inside everything in the library."
              onChoose={(chosen) => {
                setPicking(false)
                onChange({ ...draft, kind: 'document', documentId: chosen.id }, chosen.label)
              }}
              onClose={() => {
                setPicking(false)
              }}
            />
          )}
        </div>
      )}
    </li>
  )
}
